import React, { useState, useEffect, useContext } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { Layout } from '../../components/Layout'
import { Button } from 'primereact/button'
import { Message } from 'primereact/message'
import { TabView, TabPanel } from 'primereact/tabview'
// import { Tag } from 'primereact/tag' // No disponible en esta versión
import { Context } from '../../Context'
import { SchedulerService } from '../../services/Scheduler'
import './styles.scss'

export const ReservationDetails = () => {
  const { schedulerId } = useParams()
  const history = useHistory()
  const { isAuth: token } = useContext(Context)
  const [reservationData, setReservationData] = useState(null)
  const [sessionRecords, setSessionRecords] = useState([])
  const [vmFiles, setVmFiles] = useState({}) // Para almacenar los archivos de cada VM
  const [loadingVMs, setLoadingVMs] = useState({}) // Para el estado de carga de cada VM
  const [activeTabIndex, setActiveTabIndex] = useState(0) // Para controlar la pestaña activa
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (schedulerId && token) {
      getReservationDetails()
    }
  }, [schedulerId, token])

  // Cargar automáticamente la primera VM cuando se cargan los sessionRecords
  useEffect(() => {
    if (sessionRecords && sessionRecords.length > 0 && !isLoading) {
      const firstVM = sessionRecords[0]
      if (firstVM && firstVM.vm_id && !vmFiles[firstVM.vm_id]) {
        getVMFiles(firstVM.vm_id)
      }
    }
  }, [sessionRecords, isLoading])

  // Cargar archivos cuando cambie la pestaña activa
  useEffect(() => {
    if (sessionRecords && sessionRecords.length > 0 && activeTabIndex >= 0) {
      const currentVM = sessionRecords[activeTabIndex]
      if (currentVM && currentVM.vm_id) {
        getVMFiles(currentVM.vm_id)
      }
    }
  }, [activeTabIndex, sessionRecords])

  const getReservationDetails = async () => {
    try {
      setIsLoading(true)
      setError(null)

      // Obtener detalles de la reserva
      const reservationResponse = await SchedulerService.getById(schedulerId, token)
      if (reservationResponse) {
        setReservationData(reservationResponse)
      }

      // Obtener registros de sesión
      const sessionRecordsResponse = await SchedulerService.getSessionRecords(schedulerId, token)
      if (sessionRecordsResponse && Array.isArray(sessionRecordsResponse)) {
        setSessionRecords(sessionRecordsResponse)
      } else {
        setSessionRecords([])
      }
    } catch (e) {
      console.error('Error:', e)
      setError('Error al cargar los detalles de la reserva')
      setReservationData(null)
      setSessionRecords([])
    } finally {
      setIsLoading(false)
    }
  }

  const getVMFiles = async (vmId) => {
    try {
      setLoadingVMs((prev) => ({ ...prev, [vmId]: true }))

      const response = await SchedulerService.getSessionRecordsByVM(schedulerId, vmId, token)
      if (response && response.files) {
        setVmFiles((prev) => ({
          ...prev,
          [vmId]: {
            content: response.files,
            totalFiles: response.totalFiles || 0,
            foundFiles: response.foundFiles || 0,
          },
        }))
      }
    } catch (e) {
      console.error(`Error al cargar archivos de VM ${vmId}:`, e)
      setVmFiles((prev) => ({
        ...prev,
        [vmId]: {
          content: 'Error al cargar los archivos de sesión',
          totalFiles: 0,
          foundFiles: 0,
        },
      }))
    } finally {
      setLoadingVMs((prev) => ({ ...prev, [vmId]: false }))
    }
  }

  const cleanSessionContent = (content) => {
    if (!content) return ''

    // Dividir por bloques de TYPESCRIPT
    const blocks = content.split(/\[BEGIN TYPESCRIPT\]/g)
    const cleanedBlocks = []

    blocks.forEach((block) => {
      if (!block.trim()) return

      // Remover [END TYPESCRIPT] y obtener el contenido
      const cleanBlock = block.replace(/\[END TYPESCRIPT\]/g, '').trim()

      // Solo incluir bloques que tengan contenido real (no solo espacios o saltos de línea)
      if (cleanBlock && cleanBlock.length > 0) {
        // Verificar que no sea solo espacios, saltos de línea o caracteres de control
        const hasRealContent = cleanBlock
          .split('\n')
          .some(
            (line) => line.trim().length > 0 && !line.match(/^\s*$/) && !line.match(/^[\r\n\s]*$/)
          )

        if (hasRealContent) {
          cleanedBlocks.push(cleanBlock)
        }
      }
    })

    // Unir los bloques con contenido real
    return cleanedBlocks.join('\n\n---\n\n')
  }

  const downloadSessionFile = (vmData, vmName) => {
    if (!vmData || !vmData.content) return

    const cleanedContent = cleanSessionContent(vmData.content)
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)
    const filename = `session_${vmName.replace(/[^a-zA-Z0-9]/g, '_')}_${timestamp}.txt`

    // Crear el contenido del archivo con headers informativos
    const fileContent = `SESIÓN DE LABORATORIO - ${vmName}
Fecha de descarga: ${new Date().toLocaleString('es-ES')}
Total de archivos: ${vmData.foundFiles || vmData.totalFiles || 0}
========================================

${cleanedContent}

========================================
Fin de la sesión
`

    // Crear y descargar el archivo
    const blob = new Blob([fileContent], { type: 'text/plain;charset=utf-8' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="reservation-details-container">
          <div className="loading-container">
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '4em' }}></i>
            <p>Cargando detalles de la reserva...</p>
          </div>
        </div>
      </Layout>
    )
  }

  if (error) {
    return (
      <Layout>
        <div className="reservation-details-container">
          <Message severity="error" text={error} />
          <Button
            label="Volver"
            icon="pi pi-arrow-left"
            onClick={() => history.goBack()}
            className="p-button-secondary"
          />
        </div>
      </Layout>
    )
  }

  if (!reservationData) {
    return (
      <Layout>
        <div className="reservation-details-container">
          <Message severity="warn" text="No se encontraron detalles de la reserva" />
          <Button
            label="Volver"
            icon="pi pi-arrow-left"
            onClick={() => history.goBack()}
            className="p-button-secondary"
          />
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="reservation-details-container">
        <div className="reservation-details-header">
          <Button
            label="Volver"
            icon="pi pi-arrow-left"
            onClick={() => history.goBack()}
            className="p-button-secondary back-button"
          />
          <h1>
            <i className="pi pi-calendar"></i>
            Detalles de la Reserva
          </h1>
        </div>

        <div className="tabs-container">
          <TabView
            className="reservation-tabs"
            activeIndex={activeTabIndex}
            onTabChange={(e) => {
              setActiveTabIndex(e.index)
              // Cuando cambie de pestaña, cargar los archivos de la VM correspondiente
              const activeIndex = e.index
              if (sessionRecords && sessionRecords[activeIndex]) {
                const vmId = sessionRecords[activeIndex].vm_id
                if (vmId) {
                  getVMFiles(vmId)
                }
              }
            }}
          >
            {sessionRecords && sessionRecords.length > 0 ? (
              sessionRecords
                .filter((record) => record && typeof record === 'object')
                .map((record, index) => {
                  const vmId = record.vm_id
                  const isVMLoading = loadingVMs[vmId]
                  const vmFileData = vmFiles[vmId]

                  return (
                    <TabPanel
                      key={record.id || `record-${index}`}
                      header={
                        record.virtualMachine?.display_name || `VM ${record.vm_id || 'Unknown'}`
                      }
                      leftIcon="pi pi-desktop"
                    >
                      <div className="tab-content">
                        <div className="vm-header">
                          <h3>
                            {record.virtualMachine?.display_name ||
                              `VM ${record.vm_id || 'Unknown'}`}
                          </h3>
                          <div className="vm-info">
                            <span className="vm-id">ID: {vmId}</span>
                          </div>
                        </div>

                        <div className="session-content">
                          {isVMLoading ? (
                            <div className="loading-files">
                              <i
                                className="pi pi-spin pi-spinner"
                                style={{ fontSize: '2em', color: '#007ad9' }}
                              ></i>
                              <p>Cargando archivos de sesión...</p>
                            </div>
                          ) : vmFileData && vmFileData.content ? (
                            <div className="files-content">
                              <div className="files-header">
                                <h4>Contenido de la Sesión</h4>
                                <div className="files-actions">
                                  <Button
                                    label="Descargar"
                                    icon="pi pi-download"
                                    onClick={() =>
                                      downloadSessionFile(
                                        vmFileData,
                                        record.virtualMachine?.display_name || `VM_${vmId}`
                                      )
                                    }
                                    className="p-button-outlined p-button-sm download-button"
                                    tooltip="Descargar sesión como archivo .txt"
                                    tooltipOptions={{ position: 'top' }}
                                  />
                                </div>
                              </div>
                              <div className="terminal-content">
                                <pre className="terminal-text">
                                  {cleanSessionContent(vmFileData.content)}
                                </pre>
                              </div>
                            </div>
                          ) : (
                            <div className="no-content">
                              <i
                                className="pi pi-file-o"
                                style={{ fontSize: '3em', color: '#666' }}
                              ></i>
                              <p>No hay contenido de sesión disponible</p>
                              <Button
                                label="Cargar Archivos"
                                icon="pi pi-refresh"
                                onClick={() => getVMFiles(vmId)}
                                className="p-button-outlined"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </TabPanel>
                  )
                })
            ) : (
              <TabPanel header="Sin Registros" leftIcon="pi pi-exclamation-triangle">
                <div className="tab-content">
                  <div className="no-records">
                    <i className="pi pi-info-circle" style={{ fontSize: '3em', color: '#666' }}></i>
                    <p>No se encontraron registros de sesión para esta reserva</p>
                  </div>
                </div>
              </TabPanel>
            )}
          </TabView>
        </div>
      </div>
    </Layout>
  )
}
